import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useHover from '../../../hooks/useHover';
import { TripCard } from '../../../redux/trip/trip.types';
import './CardTags.scss';

interface CardTagsProps {
  card: TripCard;
  isLarge?: boolean;
  isModal?: boolean;
  isMobileModal?: boolean;
  isItinerarySidebarCard?: boolean;
}

const TAG_GAP = 4;
const OVERFLOW_WIDTH = { MODAL: 50, CARD: 40 } as const;

function CardTags({
  card,
  isLarge = false,
  isModal = false,
  isMobileModal = false,
  isItinerarySidebarCard = false,
}: CardTagsProps) {
  const tags = card.tags || [];
  const containerRef = useRef<HTMLDivElement>(null);
  // Refs for every tag chip — overflow tags stay in DOM (position:absolute) so
  // they remain measurable even when not visible in the flex row.
  const tagRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState(tags.length);
  const { isHovered: showTooltip, hoverProps } = useHover();

  const calculate = useCallback(() => {
    const container = containerRef.current;
    if (!container || tags.length === 0) {
      return;
    }

    const containerWidth = container.getBoundingClientRect().width;
    // Skip if container hasn't been laid out yet — ResizeObserver will retry.
    if (containerWidth === 0) {
      return;
    }

    const overflowReserve = isModal ? OVERFLOW_WIDTH.MODAL : OVERFLOW_WIDTH.CARD;

    // Read actual browser-rendered widths. Overflow tags use position:absolute so
    // they're out of flex flow but still in DOM and measurable.
    const tagWidths = tags.map((_, i) => {
      const el = tagRefs.current[i];
      return el ? el.getBoundingClientRect().width : 0;
    });

    // Flex gap applies N-1 times for N items.
    const totalNatural = tagWidths.reduce((s, w) => s + w, 0) + TAG_GAP * (tags.length - 1);

    // First pass: all tags fit — no overflow indicator needed.
    if (totalNatural <= containerWidth) {
      setVisibleCount(tags.length);
      return;
    }

    // Second pass: find how many fit alongside the overflow indicator.
    const available = containerWidth - overflowReserve - TAG_GAP;
    let used = 0;
    let count = 0;
    for (let i = 0; i < tagWidths.length; i++) {
      const itemWidth = tagWidths[i] + (i > 0 ? TAG_GAP : 0);
      if (used + itemWidth <= available) {
        used += itemWidth;
        count = i + 1;
      } else {
        break;
      }
    }
    setVisibleCount(Math.max(1, count));
  }, [tags, isModal]);

  // Run synchronously before paint to avoid flicker, and also on container resize.
  useLayoutEffect(() => {
    calculate();
  }, [calculate]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || tags.length === 0) {
      return;
    }
    const observer = new ResizeObserver(() => calculate());
    observer.observe(container);
    return () => observer.disconnect();
  }, [calculate, tags.length]);

  if (tags.length === 0) return null;

  const hiddenTags = tags.slice(visibleCount);
  const hasOverflow = hiddenTags.length > 0;
  const modalClass = isModal ? 'modal' : '';
  const containerClassName = [
    'card-tags-container',
    isMobileModal ? 'mobile-modal' : isModal ? 'modal' : isLarge ? 'large' : '',
    isItinerarySidebarCard ? 'itinerary-sidebar' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={containerClassName}
      ref={containerRef}
    >
      {tags.map((tag, i) => (
        <span
          key={tag}
          ref={(el) => {
            tagRefs.current[i] = el;
          }}
          className={['tag-chip', modalClass, i >= visibleCount ? 'tag-chip--hidden' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {tag}
        </span>
      ))}
      {hasOverflow && (
        <div
          className='overflow-wrapper'
          {...hoverProps}
        >
          <span className={`overflow-indicator ${modalClass}`}>+{hiddenTags.length}</span>
          {showTooltip && (
            <div className={`hidden-tags-tooltip ${modalClass}`}>
              {hiddenTags.map((tag) => (
                <span
                  key={tag}
                  className={`tooltip-tag ${modalClass}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CardTags.displayName = 'CardTags';
export default CardTags;
